// Where the design's step and aisle lights go, derived from where the seats actually are. One
// implementation for the studio and the guest map, so the two cannot drift. World meters, seats face -z.
// Pure: no imports.

type V3 = { x: number; y: number; z: number };
/** What a hint needs of a seat: its base center (y = the row's tread height) and its box. */
export type HintSeat = { position: V3; size: V3 };

export type LightingRow = {
    /** Mean z of the row's seat centers. */
    z: number;
    /** Tread height of the row (world y of the seats' base). */
    y: number;
    /** Z of the riser face: the front edge of the row's platform. */
    front: number;
    /** Left and right end of the whole row. */
    x0: number;
    x1: number;
    /** Contiguous runs of seats; an aisle splits the row, and the risers only exist under seats. */
    spans: { x0: number; x1: number }[];
};

export type LightingHints = { rows: LightingRow[]; aisles: { x: number }[] };

const ROW_TOLERANCE_M = 0.25;
const MIN_AISLE_M = 0.55;
const MAX_AISLES = 8;
const AISLE_MERGE_M = 0.35;

export function computeLightingHints(seats: HintSeat[]): LightingHints {
    const sorted = [...seats].sort((a, b) => a.position.z - b.position.z);
    const grouped: HintSeat[][] = [];
    for (const seat of sorted) {
        const last = grouped[grouped.length - 1];
        if (last && Math.abs(last[0].position.z - seat.position.z) <= ROW_TOLERANCE_M) last.push(seat);
        else grouped.push([seat]);
    }
    const aisles: { x: number }[] = [];
    const rows = grouped.map((row): LightingRow => {
        row.sort((a, b) => a.position.x - b.position.x);
        const spans: { x0: number; x1: number }[] = [];
        let spanStart = row[0].position.x - row[0].size.x / 2;
        for (let i = 1; i < row.length; i++) {
            const prev = row[i - 1];
            const next = row[i];
            const prevEdge = prev.position.x + prev.size.x / 2;
            const nextEdge = next.position.x - next.size.x / 2;
            if (nextEdge - prevEdge < MIN_AISLE_M) continue;
            const x = (prevEdge + nextEdge) / 2;
            if (!aisles.some((a) => Math.abs(a.x - x) < AISLE_MERGE_M)) aisles.push({ x });
            spans.push({ x0: spanStart, x1: prevEdge });
            spanStart = nextEdge;
        }
        const first = row[0];
        const last = row[row.length - 1];
        const lastEdge = last.position.x + last.size.x / 2;
        spans.push({ x0: spanStart, x1: lastEdge });
        return {
            z: row.reduce((sum, s) => sum + s.position.z, 0) / row.length,
            y: row.reduce((sum, s) => sum + s.position.y, 0) / row.length,
            front: Math.min(...row.map((s) => s.position.z - s.size.z / 2)),
            x0: first.position.x - first.size.x / 2,
            x1: lastEdge,
            spans
        };
    });
    return { rows, aisles: aisles.sort((a, b) => a.x - b.x).slice(0, MAX_AISLES) };
}
